import { memo } from 'react';
import { JobRowActions } from '@/components/job-feed/JobRowActions';
import type { JobRowProps } from '@/components/job-feed/jobFeed.types';
import { ScoreWithTooltip } from '@/components/ui/ScoreWithTooltip';
import { StatusGroup } from '@/components/ui/StatusGroup';
import type { StatusPillStatus } from '@/components/ui/StatusPill';
import { TechChips } from '@/components/ui/TechChips';
import { TruncatedText } from '@/components/ui/TruncatedText';
import { cx } from '@/cva.config';
import { breakdownFromJob, parseTechStack } from '@/lib/jobAdapter';
import { formatSalary } from '@/lib/score';
import {
  useHasSelection,
  useIsJobSelected,
  useSelectJob,
} from '@/providers/dashboard/JobBrowserContext';

export const JobRowComfortable = memo(({ job }: JobRowProps) => {
  const selected = useIsJobSelected(job.id);
  const hasSelection = useHasSelection();
  const select = useSelectJob();
  const hideActions = hasSelection;
  const tooltipsDisabled = hasSelection;

  const salary = formatSalary(job.salaryMin, job.salaryMax);
  const breakdown = breakdownFromJob(job);
  const tech = parseTechStack(job.techStack);

  return (
    // biome-ignore lint/a11y/useSemanticElements: the row contains its own action buttons, so it cannot be a native <button> (nested buttons are invalid HTML); role="button" + keyboard handling is the correct pattern here
    <div
      className={cx(
        'group grid cursor-pointer grid-cols-[96px_minmax(0,1fr)_120px_120px_120px] items-center gap-x-6 border-b border-border/60 px-4 py-3 transition-all hover:bg-border/20 w-full',
        hideActions && 'grid-cols-[96px_minmax(0,1fr)_220px_230px]',
        selected && 'bg-accent-soft/40 hover:bg-accent-soft/50',
      )}
      data-job-id={job.id}
      onClick={() => select(job.id)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select(job.id);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <ScoreWithTooltip
        breakdown={breakdown}
        disabled={tooltipsDisabled}
        placement="right"
        score={job.matchScore}
      />

      <div className="min-w-0">
        <div className="flex min-w-0 flex-col">
          <TruncatedText
            className="text-sm lg:text-base font-medium"
            text={job.title}
          />
          <div className="flex w-full min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-xs lg:text-sm text-foreground-muted">
              {job.company}
            </span>
            {job.location && (
              <>
                <span className="shrink-0 text-subtle">·</span>
                <span className="shrink-0 whitespace-nowrap text-subtle text-2xs lg:text-xs">
                  {job.location}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs lg:text-sm">
          {salary && (
            <span className="font-mono whitespace-nowrap text-score-high tabular-nums">
              {salary}
            </span>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <TechChips max={4} tech={tech} />
      </div>

      <div className="min-w-0">
        <StatusGroup
          appStatus={
            job.appStatus ? (job.appStatus as StatusPillStatus) : undefined
          }
          jobStatus={job.status as StatusPillStatus}
        />
      </div>

      {!hideActions && <JobRowActions job={job} variant="comfortable" />}
    </div>
  );
});

JobRowComfortable.displayName = 'JobRowComfortable';
